import React from 'react';
import { Card, Tag, Button } from 'antd';
import { Typography } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * SyncHistoryCard
 * ----------------------------------------------------------------------------
 * Renders a single SyncEntry as a card for narrow viewports.
 *
 * The info (ⓘ) button in the top-right opens the detail Drawer, matching the
 * desktop table's InfoCircleOutlined icon behaviour. The whole card is NOT
 * clickable (unlike AuditLog) — this matches SyncHistory's desktop UX where
 * only the icon opens the drawer, not the row.
 *
 * Read-only: no kebab menu.
 */

type SyncEntry = {
  id: string;
  source: string;
  status: string;
  records: number | null;
  inserted: number | null;
  updated: number | null;
  failed: number | null;
  file: string | null;
  error: string | null;
  details: unknown;
  duration: number | null;
  timestamp: string;
  completedAt: string | null;
};

const sourceLabels: Record<string, string> = {
  'VRS API': 'Data SIM sync',
  'VRS CDR': 'CDR file import',
  Giacom: 'Crew SIM sync',
};

// Short user-facing labels for the source tag. Keys are the backend
// `source` values (unchanged); only the displayed text differs. See
// KAN-48 (VRS -> Data) and KAN-49 (Giacom -> Crew).
const sourceDisplayNames: Record<string, string> = {
  'VRS API': 'Data API',
  'VRS CDR': 'Data CDR',
  Giacom: 'Crew',
};

function statusTag(status: string) {
  if (status === 'completed' || status === 'success')
    return (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        OK
      </Tag>
    );
  if (status === 'failed' || status === 'error')
    return (
      <Tag color="red" icon={<CloseCircleOutlined />}>
        Failed
      </Tag>
    );
  if (status === 'started')
    return (
      <Tag color="blue" icon={<ClockCircleOutlined />}>
        Running
      </Tag>
    );
  return <Tag icon={<ClockCircleOutlined />}>{status}</Tag>;
}

function describeEntry(r: SyncEntry): string {
  const parts: string[] = [];
  if (r.records != null && r.records > 0) parts.push(`${r.records.toLocaleString()} processed`);
  if (r.inserted != null && r.inserted > 0) parts.push(`${r.inserted.toLocaleString()} new`);
  if (r.updated != null && r.updated > 0) parts.push(`${r.updated.toLocaleString()} updated`);
  if (r.failed != null && r.failed > 0) parts.push(`${r.failed} failed`);
  if (r.file) parts.push(r.file);
  if (parts.length === 0 && r.error) return r.error.slice(0, 80);
  if (parts.length === 0) return 'No details';
  return parts.join(' · ');
}

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 13 }}>
    <span style={{ color: 'rgba(0,0,0,0.55)', minWidth: 70, flexShrink: 0 }}>{label}</span>
    <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
  </div>
);

type Props = {
  entry: SyncEntry;
  onInfoClick: (entry: SyncEntry) => void;
};

const SyncHistoryCard: React.FC<Props> = ({ entry, onInfoClick }) => {
  const desc = describeEntry(entry);
  const hasError = entry.failed != null && entry.failed > 0;

  const when = new Date(entry.timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const sourceTag = (
    <Tag
      color={
        entry.source === 'VRS API'
          ? 'blue'
          : entry.source === 'VRS CDR'
            ? 'orange'
            : entry.source === 'Giacom'
              ? 'purple'
              : 'default'
      }
      style={{ margin: 0 }}
    >
      {sourceDisplayNames[entry.source] || entry.source}
    </Tag>
  );

  return (
    <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { padding: 12 } }}>
      {/* Top row: source tag + status tag + info button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        {sourceTag}
        <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>
          {sourceLabels[entry.source] || entry.source}
        </span>
        <div style={{ flex: 1 }} />
        <Button
          type="text"
          size="small"
          icon={<InfoCircleOutlined style={{ color: '#1890ff' }} />}
          onClick={() => onInfoClick(entry)}
          aria-label="View sync details"
        />
      </div>

      <FieldRow label="When">{when}</FieldRow>
      <FieldRow label="Result">{statusTag(entry.status)}</FieldRow>
      <FieldRow label="Summary">
        {hasError ? <Text type="danger">{desc}</Text> : <span>{desc}</span>}
      </FieldRow>
    </Card>
  );
};

export default SyncHistoryCard;
